import { morphoPools } from '@1delta/data-sdk'
import { getEvmClient } from '@1delta/providers'
import { Hex, parseAbi, zeroAddress } from 'viem'
import type { MulticallRetryFunction } from '../../../types/providers'
import { isFailedCall } from '../../../utils/multicall'

/**
 * Factory for the EVM client used by the broker reads. Callers that execute against a custom RPC
 * (e.g. a fork) must pass their own factory so the per-user broker reads (`userFixedPositions`,
 * …) hit the SAME chain as the rest of the user-data multicall — otherwise the loan created on a
 * fork is invisible (the default client points at the configured mainnet RPC).
 */
type EvmClientFactory = (
  chainId: string,
  rpcId?: number,
) => ReturnType<typeof getEvmClient>

/**
 * Lista fixed-term `LendingBroker` support for the margin-fetcher.
 *
 * Brokers are the debt-side gateway of a Moolah (Morpho-fork) market: collateral lives
 * in Moolah under the user's own address (shared/flexible across all of the user's loans),
 * while borrow/repay run through the broker which overlays a single dynamic position plus
 * up to N fixed-term positions per user.
 *
 * The broker address is a *second hop* (it depends on the market), so rather than thread it
 * through the single-shot multicall we resolve it via `Moolah.brokers(id)` (immutable, cached)
 * and fetch broker data in dedicated multicalls whose results are stashed in module caches:
 *  - public fixed-term menu  -> {@link fetchListaFixedTerms} / {@link getCachedListaFixedTerms}
 *  - per-user loan breakdown -> {@link fetchListaBrokerUserData} / {@link getCachedListaBrokerUserData}
 */

// RATE_SCALE used by the broker: apr is stored as (1 + r) * 1e27
const RATE_SCALE = 10n ** 27n
const SECONDS_PER_YEAR = 365n * 24n * 60n * 60n

// ---------- ABIs ----------

const moolahBrokersAbi = parseAbi([
  'function brokers(bytes32 id) view returns (address)',
])

const brokerAbi = parseAbi([
  'function getFixedTerms() view returns (uint256[3][])',
  'function userFixedPositions(address user) view returns (uint256[8][])',
  'function userDynamicPosition(address user) view returns (uint256[2])',
  'function getUserTotalDebt(address user) view returns (uint256)',
])

// ---------- Types ----------

/** One term product offered by a broker (the borrow menu). Named generically (`ListaTerm`/`terms`)
 *  rather than "fixed" — Lista may add floating-rate terms later. */
export interface ListaTerm {
  termId: number
  durationSecs: number
  durationDays: number
  /** annualised borrow rate in PERCENT (e.g. 3.857 = 3.857% APR), matching the other rate fields */
  apr: number
}

/** One term loan tranche held by a user (debt only — collateral is shared). */
export interface ListaFixedLoan {
  /** global position id — the "loanId" consumed by the LISTA_BROKER_REPAY composer op */
  posId: string
  /** best-effort term product id, matched from the loan's duration against the current
   *  getFixedTerms menu. Not stored on-chain per loan; undefined if no menu match. */
  termId?: number
  principal: string
  /** principal - principalRepaid */
  outstanding: string
  /** annualised borrow rate in PERCENT */
  apr: number
  start: number
  end: number
  termDays: number
  /** outstanding accrued interest (gross accrued - interestRepaid), in loan-token units */
  accruedInterest: string
  /**
   * early-repayment penalty (loan-token units) charged to close the full outstanding principal
   * right now; `0` once matured. Levied on top of outstanding + accruedInterest by the broker.
   */
  earlyRepayPenalty: string
  isMatured: boolean
}

/** Aggregated broker debt for one user in one market. */
export interface ListaBrokerUserData {
  /** broker.getUserTotalDebt — principal + all interest, the authoritative debt */
  totalDebt: string
  dynamic?: { principal: string; normalizedDebt: string }
  fixedLoans: ListaFixedLoan[]
}

// ---------- Caches ----------

// chainId -> marketId(bytes32, lowercase) -> broker address. Immutable on-chain ⇒ cache forever.
const BROKER_CACHE: Record<string, Record<string, string>> = {}
// chainId -> marketId(bytes32) -> term menu
const FIXED_TERMS_CACHE: Record<string, Record<string, ListaTerm[]>> = {}
// `${chainId}:${account}` -> marketId(bytes32) -> user broker data (overwritten each fetch)
const BROKER_USER_CACHE: Record<
  string,
  Record<string, ListaBrokerUserData>
> = {}
// chainId -> in-flight menu refresh. The converter fires a warm on every call
// and `getLenderPublicData` primes explicitly; single-flighting means a cycle
// reads each broker once, not once per caller.
const FIXED_TERMS_INFLIGHT: Record<
  string,
  Promise<Record<string, ListaTerm[]>>
> = {}

// ---------- Helpers ----------

/** Normalise a market key ('LISTA_DAO_<HEX>' or '0x<hash>') to a lowercase bytes32 id. */
export function toBytes32MarketId(key: string): Hex {
  const raw = key.startsWith('LISTA_DAO_')
    ? '0x' + key.slice('LISTA_DAO_'.length)
    : key
  return raw.toLowerCase() as Hex
}

function ceilDiv(a: bigint, b: bigint): bigint {
  if (b === 0n) return 0n
  return (a + b - 1n) / b
}

/** annualised rate in PERCENT from the raw on-chain apr ((1+r)*RATE_SCALE) */
function aprPercentFromRaw(apr: bigint): number {
  // apr stored as (1 + r) * RATE_SCALE; below RATE_SCALE means 0
  if (apr <= RATE_SCALE) return 0
  return (Number(apr - RATE_SCALE) / Number(RATE_SCALE)) * 100
}

function aprPerSecond(apr: bigint): bigint {
  if (apr <= RATE_SCALE) return 0n
  return ceilDiv(apr - RATE_SCALE, SECONDS_PER_YEAR)
}

/**
 * Run the broker reads through the CALLER's multicall when it hands one over,
 * and only fall back to the SDK's default client when it doesn't.
 *
 * The distinction is the whole fix for "Lista terms never reach the origin":
 * the lending cron executes its market multicall through `multicallRetry`
 * built from the box's `RPC_<chainId>` overrides, while these reads used to go
 * through `getEvmClient(chainId)` — the registry default (thirdweb / merkle) —
 * unconditionally, from inside a `catch {}`. A box whose default endpoint
 * rate-limits or blocks it therefore fetched every Moolah market fine and
 * never once populated a broker menu, with nothing in the logs. Failures are
 * per slot: a broker that reverts (an older implementation without
 * `getFixedTerms`) yields `undefined` in its position and never fails the batch.
 */
async function readBrokerContracts<T>(
  chainId: string,
  contracts: { address: Hex; functionName: string; args: unknown[] }[],
  multicallRetry?: MulticallRetryFunction,
  getClient: EvmClientFactory = getEvmClient,
): Promise<(T | undefined)[]> {
  if (contracts.length === 0) return []
  const abi = [...moolahBrokersAbi, ...brokerAbi]
  if (multicallRetry) {
    const raw = (await multicallRetry({
      chain: chainId,
      calls: contracts.map((c) => ({
        address: c.address,
        name: c.functionName,
        params: c.args,
      })),
      abi: contracts.map(() => abi),
      allowFailure: true,
    })) as unknown[]
    return contracts.map((_, i) =>
      isFailedCall(raw?.[i]) ? undefined : (raw[i] as T),
    )
  }
  const client = getClient(chainId)
  const results = await client.multicall({
    allowFailure: true,
    contracts: contracts.map((c) => ({
      abi,
      address: c.address,
      functionName: c.functionName,
      args: c.args,
    })) as any,
  })
  return results.map((r) =>
    r.status === 'success' && r.result != null ? (r.result as T) : undefined,
  )
}

/** Outstanding accrued interest for a fixed position — mirrors BrokerMath. */
function accruedInterest(
  outstanding: bigint,
  apr: bigint,
  start: bigint,
  end: bigint,
  lastRepaidTime: bigint,
  interestRepaid: bigint,
  nowSecs: bigint,
): bigint {
  const cap = nowSecs > end ? end : nowSecs
  const from = lastRepaidTime > end ? end : lastRepaidTime
  if (outstanding === 0n || cap <= from) return 0n
  const timeElapsed = cap - from
  const gross = ceilDiv(
    outstanding * aprPerSecond(apr) * timeElapsed,
    RATE_SCALE,
  )
  return gross > interestRepaid ? gross - interestRepaid : 0n
}

/**
 * Early-repayment penalty for repaying `repayPrincipal` of a fixed position now — mirrors
 * BrokerMath.getPenaltyForFixedPosition. Zero once matured (now >= end). Roughly half the
 * interest that principal would still accrue over the remaining term.
 */
function earlyRepayPenalty(
  repayPrincipal: bigint,
  apr: bigint,
  end: bigint,
  nowSecs: bigint,
): bigint {
  if (repayPrincipal === 0n || nowSecs >= end) return 0n
  const timeLeft = end - nowSecs
  // ceil(ceil(repayPrincipal * aprPerSecond / RATE_SCALE) * timeLeft / 2)
  const perRepay = ceilDiv(repayPrincipal * aprPerSecond(apr), RATE_SCALE)
  return ceilDiv(perRepay * timeLeft, 2n)
}

// ---------- Broker resolution ----------

/**
 * Resolve the broker address for each Lista market via `Moolah.brokers(id)`.
 * Returns a map keyed by the bytes32 market id (lowercase). Results are cached forever
 * (broker registration is immutable per market). Markets without a broker resolve to undefined.
 */
export async function resolveListaBrokers(
  chainId: string,
  marketKeys: string[],
  getClient: EvmClientFactory = getEvmClient,
  multicallRetry?: MulticallRetryFunction,
): Promise<Record<string, string>> {
  const moolah = morphoPools()?.LISTA_DAO?.[chainId]
  const out: Record<string, string> = {}
  if (!moolah || marketKeys.length === 0) return out

  const cache = (BROKER_CACHE[chainId] ??= {})
  const missing: Hex[] = []
  for (const key of marketKeys) {
    const id = toBytes32MarketId(key)
    if (cache[id] === undefined) missing.push(id)
  }

  if (missing.length > 0) {
    try {
      const results = await readBrokerContracts<string>(
        chainId,
        missing.map((id) => ({
          address: moolah as Hex,
          functionName: 'brokers',
          args: [id],
        })),
        multicallRetry,
        getClient,
      )
      results.forEach((addr, i) => {
        // A failed slot stays UNCACHED so the next cycle retries it; caching
        // zeroAddress here would demote a brokered market to a plain Moolah
        // market for the life of the process.
        if (addr === undefined) return
        cache[missing[i]] = addr.toLowerCase()
      })
    } catch (e) {
      console.warn(
        `[lista] chain ${chainId}: Moolah.brokers() read failed for ${missing.length} markets — brokered markets will carry no term menu this cycle:`,
        (e as any)?.shortMessage ?? (e as any)?.message ?? e,
      )
    }
  }

  for (const key of marketKeys) {
    const id = toBytes32MarketId(key)
    const broker = cache[id]
    if (broker && broker !== zeroAddress) out[id] = broker
  }
  return out
}

// ---------- Public: fixed-term menu ----------

/**
 * Fetch the fixed-term menu (`getFixedTerms`) for every brokered Lista market and cache it.
 * Returns a map keyed by the bytes32 market id.
 *
 * Pass the pipeline's `multicallRetry` wherever one exists (the lending cron
 * does, via `getLenderPublicData`) so the reads use the same endpoints as the
 * market fetch; without it the SDK default client is used. Concurrent calls for
 * one chain share a single in-flight request.
 *
 * This must be AWAITED before the (synchronous) Lista converter runs — the
 * converter only reads the cache. A fire-and-forget warm cannot serve the
 * cycle that started it, and a process whose warm keeps failing never serves
 * a menu at all; see `getLenderPublicData` for the priming call.
 */
export async function fetchListaFixedTerms(
  chainId: string,
  marketKeys: string[],
  multicallRetry?: MulticallRetryFunction,
): Promise<Record<string, ListaTerm[]>> {
  const pending = FIXED_TERMS_INFLIGHT[chainId]
  if (pending) return pending
  const run = (async () => {
    const brokers = await resolveListaBrokers(
      chainId,
      marketKeys,
      getEvmClient,
      multicallRetry,
    )
    const ids = Object.keys(brokers)
    const out: Record<string, ListaTerm[]> = {}
    if (ids.length === 0) return out

    try {
      const results = await readBrokerContracts<readonly (readonly bigint[])[]>(
        chainId,
        ids.map((id) => ({
          address: brokers[id] as Hex,
          functionName: 'getFixedTerms',
          args: [],
        })),
        multicallRetry,
      )
      const cache = (FIXED_TERMS_CACHE[chainId] ??= {})
      results.forEach((raw, i) => {
        if (!raw) return
        const id = ids[i]
        const terms = raw.map((t) => {
          const durationSecs = Number(t[1])
          return {
            termId: Number(t[0]),
            durationSecs,
            durationDays: durationSecs / 86400,
            apr: aprPercentFromRaw(t[2]),
          }
        })
        out[id] = terms
        cache[id] = terms
      })
    } catch (e) {
      console.warn(
        `[lista] chain ${chainId}: getFixedTerms() read failed for ${ids.length} brokers — serving the previous menu, if any:`,
        (e as any)?.shortMessage ?? (e as any)?.message ?? e,
      )
    }
    return out
  })()
  FIXED_TERMS_INFLIGHT[chainId] = run
  try {
    return await run
  } finally {
    delete FIXED_TERMS_INFLIGHT[chainId]
  }
}

/** Read the cached fixed-term menu for a market (warm via {@link fetchListaFixedTerms}). */
export function getCachedListaFixedTerms(
  chainId: string,
  marketKey: string,
): ListaTerm[] | undefined {
  return FIXED_TERMS_CACHE[chainId]?.[toBytes32MarketId(marketKey)]
}

// ---------- User: per-loan breakdown ----------

/**
 * Fetch the per-user broker debt breakdown (fixed loans + dynamic + total debt) for every
 * brokered market and stash it in the per-account cache for the sync converter to read.
 */
export async function fetchListaBrokerUserData(
  chainId: string,
  account: string,
  marketKeys: string[],
  nowSecs: number = Math.floor(Date.now() / 1000),
  getClient: EvmClientFactory = getEvmClient,
): Promise<Record<string, ListaBrokerUserData>> {
  const brokers = await resolveListaBrokers(chainId, marketKeys, getClient)
  const ids = Object.keys(brokers)
  const out: Record<string, ListaBrokerUserData> = {}
  const cacheKey = `${chainId}:${account.toLowerCase()}`
  BROKER_USER_CACHE[cacheKey] = out
  if (ids.length === 0) return out

  try {
    const client = getClient(chainId)
    // four reads per brokered market: fixed positions, dynamic position, total debt, term menu
    const contracts = ids.flatMap((id) => {
      const address = brokers[id] as Hex
      return [
        {
          abi: brokerAbi,
          address,
          functionName: 'userFixedPositions' as const,
          args: [account as Hex],
        },
        {
          abi: brokerAbi,
          address,
          functionName: 'userDynamicPosition' as const,
          args: [account as Hex],
        },
        {
          abi: brokerAbi,
          address,
          functionName: 'getUserTotalDebt' as const,
          args: [account as Hex],
        },
        {
          abi: brokerAbi,
          address,
          functionName: 'getFixedTerms' as const,
          args: [],
        },
      ]
    })
    const results = await client.multicall({ allowFailure: true, contracts })
    const now = BigInt(nowSecs)

    ids.forEach((id, marketIdx) => {
      const base = marketIdx * 4
      const fixedRes = results[base]
      const dynRes = results[base + 1]
      const debtRes = results[base + 2]
      const termsRes = results[base + 3]

      // map term duration (days) → termId from the current menu (best-effort)
      const termIdByDays: Record<number, number> = {}
      if (termsRes?.status === 'success' && termsRes.result) {
        for (const t of termsRes.result as readonly (readonly bigint[])[]) {
          termIdByDays[Math.round(Number(t[1]) / 86400)] = Number(t[0])
        }
      }

      const fixedLoans: ListaFixedLoan[] = []
      if (fixedRes?.status === 'success' && fixedRes.result) {
        for (const p of fixedRes.result as readonly (readonly bigint[])[]) {
          const principal = p[1]
          const apr = p[2]
          const start = p[3]
          const end = p[4]
          const lastRepaidTime = p[5]
          const interestRepaid = p[6]
          const principalRepaid = p[7]
          const outstanding =
            principal > principalRepaid ? principal - principalRepaid : 0n
          const termDays = Math.round((Number(end) - Number(start)) / 86400)
          fixedLoans.push({
            posId: p[0].toString(),
            termId: termIdByDays[termDays],
            principal: principal.toString(),
            outstanding: outstanding.toString(),
            apr: aprPercentFromRaw(apr),
            start: Number(start),
            end: Number(end),
            termDays,
            accruedInterest: accruedInterest(
              outstanding,
              apr,
              start,
              end,
              lastRepaidTime,
              interestRepaid,
              now,
            ).toString(),
            earlyRepayPenalty: earlyRepayPenalty(
              outstanding,
              apr,
              end,
              now,
            ).toString(),
            isMatured: now >= end,
          })
        }
      }

      let dynamic: ListaBrokerUserData['dynamic']
      if (dynRes?.status === 'success' && dynRes.result) {
        const d = dynRes.result as readonly bigint[]
        if (d[0] > 0n || d[1] > 0n) {
          dynamic = {
            principal: d[0].toString(),
            normalizedDebt: d[1].toString(),
          }
        }
      }

      const totalDebt =
        debtRes?.status === 'success' && debtRes.result != null
          ? (debtRes.result as bigint).toString()
          : '0'

      if (fixedLoans.length > 0 || dynamic || totalDebt !== '0') {
        out[id] = { totalDebt, dynamic, fixedLoans }
      }
    })
  } catch {
    // best-effort: leave the (empty) cache entry
  }
  return out
}

/** Read the cached per-user broker data for a market (warm via {@link fetchListaBrokerUserData}). */
export function getCachedListaBrokerUserData(
  chainId: string,
  account: string,
  marketKey: string,
): ListaBrokerUserData | undefined {
  return BROKER_USER_CACHE[`${chainId}:${account.toLowerCase()}`]?.[
    toBytes32MarketId(marketKey)
  ]
}
